import { debugLog, debugError } from '../utils/debugUtils';

const DB_NAME = 'concordare-db';
const DB_VERSION = 1;
const STORE_NAME = 'wordsByTopic';
const MAX_WORDS = 5;

class GameStateManager {
  constructor() {
    this.db = null;
    this.isReady = false;
    this._initPromise = null;
  }

  async init() {
    if (this.isReady) return true;
    if (this._initPromise) return this._initPromise;

    this._initPromise = new Promise((resolve, reject) => {
      if (!window.indexedDB) {
        debugError('[GameStateManager] IndexedDB not supported');
        reject(new Error('IndexedDB not supported'));
        return;
      }

      const request = window.indexedDB.open(DB_NAME, DB_VERSION);

      request.onupgradeneeded = (event) => {
        const db = event.target.result;
        if (!db.objectStoreNames.contains(STORE_NAME)) {
          db.createObjectStore(STORE_NAME, { keyPath: 'key' });
          debugLog('[GameStateManager] Object store created');
        }
      };

      request.onsuccess = (event) => {
        this.db = event.target.result;
        this.isReady = true;
        debugLog('[GameStateManager] DB ready');
        resolve(true);
      };

      request.onerror = (event) => {
        debugError('[GameStateManager] DB open error:', event.target.error);
        reject(event.target.error);
      };
    });

    return this._initPromise;
  }

  _key(userId, topicId) {
    return `${userId}_${topicId}`;
  }

  async getWords(userId, topicId) {
    await this.init();
    return new Promise((resolve, reject) => {
      const tx = this.db.transaction(STORE_NAME, 'readonly');
      const store = tx.objectStore(STORE_NAME);
      const request = store.get(this._key(userId, topicId));
      request.onsuccess = () => resolve(request.result?.words || []);
      request.onerror = () => reject(request.error);
    });
  }

  async saveWord(userId, topicId, word) {
    await this.init();
    const existingWords = await this.getWords(userId, topicId);
    const normalized = word.trim().toLowerCase();

    if (existingWords.length >= MAX_WORDS) {
      throw new Error('MAX_WORDS_REACHED');
    }
    if (existingWords.map((w) => w.toLowerCase()).includes(normalized)) {
      throw new Error('DUPLICATE_WORD');
    }

    const updatedWords = [...existingWords, word.trim()];

    return new Promise((resolve, reject) => {
      const tx = this.db.transaction(STORE_NAME, 'readwrite');
      const store = tx.objectStore(STORE_NAME);
      const request = store.put({ key: this._key(userId, topicId), words: updatedWords });
      request.onsuccess = () => {
        debugLog('[GameStateManager] Word saved:', word.trim());
        resolve(updatedWords);
      };
      request.onerror = () => reject(request.error);
    });
  }

  async syncWords(userId, topicId, words) {
    await this.init();
    return new Promise((resolve, reject) => {
      const tx = this.db.transaction(STORE_NAME, 'readwrite');
      const store = tx.objectStore(STORE_NAME);
      const request = store.put({ key: this._key(userId, topicId), words });
      request.onsuccess = () => {
        debugLog('[GameStateManager] Words synced from server:', words);
        resolve();
      };
      request.onerror = () => reject(request.error);
    });
  }

  async clearWords(userId, topicId) {
    await this.init();
    return new Promise((resolve, reject) => {
      const tx = this.db.transaction(STORE_NAME, 'readwrite');
      const store = tx.objectStore(STORE_NAME);
      const request = store.delete(this._key(userId, topicId));
      request.onsuccess = () => {
        debugLog('[GameStateManager] Words cleared for', this._key(userId, topicId));
        resolve();
      };
      request.onerror = () => reject(request.error);
    });
  }

  async hasPlayedToday(userId, topicId) {
    const words = await this.getWords(userId, topicId);
    return words.length >= MAX_WORDS;
  }
}

export default new GameStateManager();
export { MAX_WORDS };
